import { useCallback, useRef, useState } from 'react';
import {
  TITLE_LANGS,
  TITLE_LANG_NAMES,
  moveTitleLang,
  type TitleLang,
} from '@movienight/shared';
import { useTitleOrderControls } from '../lib/TitleOrderProvider';
import './lang-sign.css';

/**
 * The pavement sign — "jezik naslova" (DESIGN.md §3a).
 *
 * Three slats in a rail. The one in the top slot is the language you get; the
 * two under it are what the site falls back to when a film has no title in the
 * one above. Drag a slat to a different slot, or tap it to send it to the top.
 *
 * ## Why an object and not a settings page
 *
 * There is no settings page on this site and this was not the feature to open
 * one with. Every other preference here is something you *do* to a thing —
 * you pay a guard, you drag the glasses onto your face, you draw on the tower.
 * A three-item priority list is a genuinely fiddly control (radio buttons
 * cannot express an order, and a `<select>` per rank can express contradictory
 * ones), and a letterboard rack is the one interface where "first, then, then"
 * needs no label at all: the top slat is on top.
 *
 * It stands below the board on every page rather than in a menu, because it is
 * one of the few controls whose effect is visible *behind it* while you use it
 * — move Serbian down and the cards above change while your finger is still on
 * the slat, which is most of the explanation anybody needs.
 *
 * ## The geometry lives here, not in the stylesheet
 *
 * `SLAT_H` and `SLAT_GAP` are TypeScript constants pushed into CSS variables,
 * because the drag arithmetic and the layout must not be able to disagree: the
 * slot a slat lands in is computed from the same pitch the CSS lays them out
 * with. The corkboard learned this one — `PinString` draws from the numbers the
 * layout used, never from measured DOM, or it lags a frame behind.
 */

const SLAT_H = 42;
const SLAT_GAP = 7;
const PITCH = SLAT_H + SLAT_GAP;

/** Below this, a pointer press was a tap, and a tap means "put this on top". */
const TAP_SLOP = 5;

interface Drag {
  lang: TitleLang;
  pointerId: number;
  startY: number;
  dy: number;
  moved: boolean;
}

const RANKS = ['1', '2', '3'];

export function LangSign() {
  const { order, reorder, error } = useTitleOrderControls();
  const [drag, setDrag] = useState<Drag | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const railRef = useRef<HTMLDivElement>(null);

  /**
   * Where each slat sits *right now*. While a slat is being dragged the others
   * move to the order they would take if it were dropped here — so the rack
   * answers the question before the finger lifts, rather than rearranging
   * itself afterwards as a surprise.
   */
  const slotOf = (lang: TitleLang): number => {
    if (!drag) return order.indexOf(lang);
    const from = order.indexOf(drag.lang);
    const target = targetSlot(from, drag.dy);
    if (lang === drag.lang) return target;
    return moveTitleLang(order, drag.lang, target).indexOf(lang);
  };

  const announce = useCallback((next: readonly TitleLang[]) => {
    setSaid(next.map((lang, i) => `${i + 1}. ${TITLE_LANG_NAMES[lang]}`).join(', '));
  }, []);

  const move = useCallback(
    (lang: TitleLang, toIndex: number) => {
      const next = moveTitleLang(order, lang, toIndex);
      if (next.join(',') === order.join(',')) return;
      reorder(next);
      announce(next);
    },
    [order, reorder, announce],
  );

  return (
    <aside className="lang-sign" aria-labelledby="lang-sign-title">
      <div className="lang-sign__board">
        <p className="lang-sign__cap" id="lang-sign-title">
          jezik naslova
        </p>

        <div
          className="lang-sign__rail"
          ref={railRef}
          style={{
            // The single source of truth for the pitch, shared with the drag
            // arithmetic above.
            ['--slat-h' as string]: `${SLAT_H}px`,
            ['--slat-gap' as string]: `${SLAT_GAP}px`,
            height: `${TITLE_LANGS.length * PITCH - SLAT_GAP}px`,
          }}
        >
          {TITLE_LANGS.map((lang) => {
            const slot = slotOf(lang);
            const held = drag?.lang === lang;
            /**
             * A slat sits at its slot — except the one in hand, which sits
             * where the finger put it: its *original* slot plus the drag, so
             * it does not jump the moment the slot under it changes. The rank
             * stamped on it is the target slot's, because that is the answer
             * it is about to give.
             */
            const offset = held
              ? order.indexOf(lang) * PITCH + drag.dy
              : slot * PITCH;

            return (
              <button
                key={lang}
                type="button"
                className={`lang-sign__slat${slot === 0 ? ' is-first' : ''}${held ? ' is-held' : ''}`}
                style={{ transform: `translateY(${offset}px)` }}
                aria-label={`${TITLE_LANG_NAMES[lang]} — ${slot + 1}. mesto`}
                title="Prevuci, ili klikni da ide na vrh"
                onPointerDown={(event) => {
                  // Only the primary button, and never while another slat is
                  // already in hand: two fingers on two slats would each
                  // compute a target against an order the other is moving.
                  if (event.button !== 0 || drag) return;
                  event.currentTarget.setPointerCapture(event.pointerId);
                  setDrag({
                    lang,
                    pointerId: event.pointerId,
                    startY: event.clientY,
                    dy: 0,
                    moved: false,
                  });
                }}
                onPointerMove={(event) => {
                  if (!drag || drag.pointerId !== event.pointerId || drag.lang !== lang) return;
                  const dy = event.clientY - drag.startY;
                  setDrag({ ...drag, dy, moved: drag.moved || Math.abs(dy) > TAP_SLOP });
                }}
                onPointerUp={(event) => {
                  if (!drag || drag.pointerId !== event.pointerId || drag.lang !== lang) return;
                  const from = order.indexOf(lang);
                  // A tap is a promotion. It is the whole control on a phone,
                  // where dragging a 42px slat with a thumb over the top of it
                  // is a worse gesture than tapping the one you want.
                  const to = drag.moved ? targetSlot(from, drag.dy) : 0;
                  setDrag(null);
                  move(lang, to);
                }}
                onPointerCancel={() => setDrag(null)}
                onKeyDown={(event) => {
                  const from = order.indexOf(lang);
                  if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    move(lang, from - 1);
                  } else if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    move(lang, from + 1);
                  } else if (event.key === 'Home') {
                    event.preventDefault();
                    move(lang, 0);
                  }
                  // Enter and Space are left to the button's own click, which
                  // the pointer handlers above do not consume for the keyboard.
                }}
                onClick={(event) => {
                  // Pointer devices have already been answered on pointerup;
                  // this is the keyboard's Enter/Space, and a click synthesised
                  // by assistive tech. `detail === 0` is how they tell apart.
                  if (event.detail !== 0) return;
                  move(lang, 0);
                }}
              >
                <span className="lang-sign__rank" aria-hidden="true">
                  {RANKS[slot]}
                </span>
                <span className="lang-sign__name">{TITLE_LANG_NAMES[lang]}</span>
              </button>
            );
          })}
        </div>

        <p className="lang-sign__hint">
          {error ?? 'prvi red je ono što vidiš — ostalo je rezerva'}
        </p>
      </div>

      <div className="lang-sign__legs" aria-hidden="true" />

      {/* Screen readers get the whole order, not "moved up": the point of the
          control is the ranking, and "Srpski, 2. mesto" alone does not say what
          is above it now. */}
      <p className="lang-sign__said" aria-live="polite">
        {said}
      </p>
    </aside>
  );
}

/** Which slot a slat dragged this far from `from` would land in. */
function targetSlot(from: number, dy: number): number {
  const slot = Math.round((from * PITCH + dy) / PITCH);
  return Math.max(0, Math.min(TITLE_LANGS.length - 1, slot));
}
